import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
  StackDivider,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Badge
} from '@chakra-ui/react';
import Layout from './layout';
import useDebounce from '@hooks/useDebounce';
import useDidMountEffect from '@hooks/useDidMount';
import Pagination from '@components/Pagination';
import FilterSelect from '@components/FiltersBox/FilterSelect';
import SearchBox from '@components/SearchBox/SearchBox';
import CopyableText from '@components/CopyableText';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import {
  OUTBOUND_CALLING_DASHBOARD_STRINGS,
  TAB_STATES,
  ADMIN_DASHBOARD_PAGE_SIZE,
  CLOSED_TICKET_STATUSES,
  TIME_RANGE_OPTIONS,
  CLOSED_TICKETS_TIME_RANGE,
  ADMIN_DASHBOARD_DEBOUNCE_DELAY_MS
} from '@views/OutboundCallingDashboard/constant';
import { SORT_DIRECTION } from '@constants';
import { INPUT_FIELD_TYPE } from '@components/DynamicFormField/constant';
import { fetchClosedTickets, fetchAvailableAgents } from '@store/actions/outboundCallingActions';
import { getStatusColor, getAdminDashboardSortOptions } from '@views/OutboundCallingDashboard/utils';
import { formatDate } from '@utils/dateUtils';

const ClosedTicketsDashboard = () => {
  const dispatch = useDispatch();
  const CONSTS = OUTBOUND_CALLING_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.ADMIN];

  // Redux state
  const { data: closedData, isLoading, error } = useSelector(
    (state) => state.outboundCalling?.adminDashboard?.closedTickets || {
      data: null,
      isLoading: false,
      error: null
    }
  );

  const { data: agentsData } = useSelector(
    (state) => state.outboundCalling?.adminDashboard?.availableAgents || {
      data: null,
      isLoading: false,
      error: null
    }
  );

  const tickets = closedData?.data || [];
  const agents = agentsData?.agents || [];
  const totalPages = closedData?.totalPages || 0;
  const totalResults = closedData?.totalResults || 0;

  // Local state for filters, sort, time range, and pagination
  const [filterParams, setFilterParams] = useState({
    status: CLOSED_TICKET_STATUSES,
    agentId: '',
    ticketId: ''
  });
  const [sortParams, setSortParams] = useState({
    sortBy: 'updatedAt',
    sortDir: SORT_DIRECTION.DESC
  });
  const [lastHours, setLastHours] = useState(CLOSED_TICKETS_TIME_RANGE.FORTY_EIGHT_HOURS);
  const [pageNumber, setPageNumber] = useState(0);

  // Debounced params
  const debouncedFilterParams = useDebounce(filterParams, ADMIN_DASHBOARD_DEBOUNCE_DELAY_MS);
  const debouncedSortParams = useDebounce(sortParams, ADMIN_DASHBOARD_DEBOUNCE_DELAY_MS);
  const debouncedLastHours = useDebounce(lastHours, ADMIN_DASHBOARD_DEBOUNCE_DELAY_MS);

  /**
   * Fetch closed tickets
   */
  const fetchClosed = useCallback(() => {
    dispatch(
      fetchClosedTickets({
        status: debouncedFilterParams.status,
        lastHours: debouncedLastHours,
        sortBy: debouncedSortParams.sortBy,
        sortDir: debouncedSortParams.sortDir,
        page: pageNumber,
        pageSize: ADMIN_DASHBOARD_PAGE_SIZE,
        agentId: debouncedFilterParams.agentId,
        ticketId: debouncedFilterParams.ticketId
      })
    );
  }, [dispatch, debouncedFilterParams, debouncedSortParams, debouncedLastHours, pageNumber]);

  /**
   * Fetch agents data
   */
  const fetchAgents = useCallback(() => {
    dispatch(fetchAvailableAgents());
  }, [dispatch]);

  /**
   * Fetch on mount and when params change
   */
  useEffect(() => {
    fetchClosed();
  }, [fetchClosed]);

  /**
   * Fetch agents on mount
   */
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  /**
   * Reset to page 0 when filters, sort, or time range change
   */
  useDidMountEffect(() => {
    setPageNumber(0);
  }, [debouncedFilterParams, debouncedSortParams, debouncedLastHours]);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback(({ key, filters }) => {
    setFilterParams((prev) => ({
      ...prev,
      [key]: filters
    }));
  }, []);

  /**
   * Handle agent change
   */
  const handleAgentChange = useCallback((e) => {
    setFilterParams((prev) => ({
      ...prev,
      agentId: e.target.value
    }));
  }, []);

  /**
   * Handle search change
   */
  const handleSearchChange = useCallback(({ searchValue }) => {
    setFilterParams((prev) => ({
      ...prev,
      ticketId: searchValue
    }));
  }, []);

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback((e) => {
    const [sortBy, sortDir] = e.target.value.split('-');
    setSortParams({ sortBy, sortDir });
  }, []);

  /**
   * Handle time range change
   */
  const handleTimeRangeChange = useCallback((e) => {
    setLastHours(parseInt(e.target.value, 10));
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page) => {
    setPageNumber(page - 1); // Convert 1-indexed to 0-indexed
  }, []);

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          {CONSTS.LOADING_CLOSED_TICKETS}
        </Text>
      </VStack>
    </Flex>
  );

  /**
   * Render error state
   */
  const renderErrorState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="red.500"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="red.400" fontSize="lg" fontWeight="bold">
        {CONSTS.ERROR_LOADING_CLOSED_TICKETS}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {error || CONSTS.NO_CLOSED_TICKETS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="whiteAlpha.700" fontSize="lg">
        {CONSTS.NO_CLOSED_TICKETS_FOUND}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {CONSTS.NO_CLOSED_TICKETS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render tickets table
   */
  const renderTable = () => (
    <TableContainer w="100%">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr
            bg="backGround"
            borderBottom="2px solid"
            borderColor="whiteAlpha.500"
          >
            <Th color="whiteAlpha.800">{CONSTS.TICKET_ID}</Th>
            <Th color="whiteAlpha.800">{CONSTS.INTERCOM_TICKET_ID}</Th>
            <Th color="whiteAlpha.800">{CONSTS.AGENT_ASSIGNED}</Th>
            <Th color="whiteAlpha.800">{CONSTS.TICKET_STATUS}</Th>
            <Th color="whiteAlpha.800">{CONSTS.LAST_UPDATED_AT}</Th>
            <Th color="whiteAlpha.800">{CONSTS.CALLBACK_ATTEMPTS}</Th>
            <Th color="whiteAlpha.800">{CONSTS.QUEUE_ID}</Th>
            <Th color="whiteAlpha.800">{CONSTS.CREATED_AT}</Th>
            <Th color="whiteAlpha.800">{CONSTS.ASSIGNED_AT}</Th>
            <Th color="whiteAlpha.800">{CONSTS.ON_CALL_AT}</Th>
            <Th color="whiteAlpha.800">{CONSTS.CANCELLED_AT}</Th>
            <Th color="whiteAlpha.800">{CONSTS.RESOLVED_AT}</Th>
            <Th color="whiteAlpha.800">{CONSTS.FAILED_AT}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {tickets.map((ticket) => (
            <Tr
              key={ticket.ticketId}
              _hover={{ bg: 'whiteAlpha.50' }}
              borderBottom="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Td>
                <CopyableText title={CONSTS.TICKET_ID_LABEL} color="whiteAlpha.900" fontWeight="medium">
                  {ticket.ticketId}
                </CopyableText>
              </Td>
              <Td>
                <CopyableText title={CONSTS.INTERCOM_TICKET_ID} color="whiteAlpha.700">
                  {ticket.intercomTicketId || CONSTS.NA}
                </CopyableText>
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.agentName || ticket.alphadeskAgentId || CONSTS.NA}
              </Td>
              <Td>
                <Badge
                  colorScheme={getStatusColor(ticket.status)}
                  fontSize="xs"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {ticket.status}
                </Badge>
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.updatedAt ? formatDate(ticket.updatedAt) : CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.callbackAttempts !== undefined ? ticket.callbackAttempts : 0}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.queueId || CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.createdAt ? formatDate(ticket.createdAt) : CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.assignedAt ? formatDate(ticket.assignedAt) : CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.onCallAt ? formatDate(ticket.onCallAt) : CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.cancelledAt ? formatDate(ticket.cancelledAt) : CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.resolvedAt ? formatDate(ticket.resolvedAt) : CONSTS.NA}
              </Td>
              <Td color="whiteAlpha.700">
                {ticket.failedAt ? formatDate(ticket.failedAt) : CONSTS.NA}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  /**
   * Render filters and sort section
   */
  const renderFiltersAndSort = () => (
    <HStack spacing={4} align="stretch" flexWrap="wrap">
      <Box minW="300px">
        <SearchBox
          searches={[
            {
              searchKey: 'ticketId',
              display: CONSTS.TICKET_ID_LABEL,
              placeholder: CONSTS.SEARCH_BY_TICKET_ID,
              isDefault: true
            }
          ]}
          handleSearchChange={handleSearchChange}
        />
      </Box>
      <FilterSelect
        filtersData={[
          {
            key: 'status',
            title: CONSTS.TICKET_STATUS,
            filters: CLOSED_TICKET_STATUSES
          }
        ]}
        handleFilterSubmit={handleFilterChange}
        defaultFiltersData={filterParams}
      />
      <Box>
        <DynamicFormField
          fieldConfig={{
            name: 'agentId',
            fieldType: INPUT_FIELD_TYPE.SELECT,
            placeholder: CONSTS.ALL_AGENTS,
            options: agents.map((agent) => ({
              value: agent.agentId,
              label: agent.agentName || agent.agentId
            })),
            isEditable: true
          }}
          data={filterParams}
          handleChange={handleAgentChange}
        />
      </Box>
      <Box>
        <DynamicFormField
          fieldConfig={{
            name: 'lastHours',
            fieldType: INPUT_FIELD_TYPE.SELECT,
            options: TIME_RANGE_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label
            })),
            isEditable: true
          }}
          data={{ lastHours }}
          handleChange={handleTimeRangeChange}
        />
      </Box>
      <Box>
        <DynamicFormField
          fieldConfig={{
            name: 'sort',
            fieldType: INPUT_FIELD_TYPE.SELECT,
            options: getAdminDashboardSortOptions('closed').map((option) => ({
              value: option.value,
              label: option.label
            })),
            isEditable: true
          }}
          data={{ sort: `${sortParams.sortBy}-${sortParams.sortDir}` }}
          handleChange={handleSortChange}
        />
      </Box>
    </HStack>
  );

  /**
   * Render pagination with results count
   */
  const renderPagination = () => {
    if (totalPages <= 1 && totalResults === 0) return null;

    return (
      <Flex justify="space-between" align="center" mt={4} mb={8} px={4}>
        <Text fontSize="sm" color="whiteAlpha.600">
          Showing {tickets.length} of {totalResults} tickets
        </Text>
        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={pageNumber + 1}
            onPageChange={handlePageChange}
          />
        )}
      </Flex>
    );
  };

  return (
    <Layout>
      <VStack
        overflowY="initial"
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
        px={4}
        pt={4}
        w="100%"
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        <Heading size="lg" color="colorPrimary">
          {CONSTS.CLOSED_TICKETS_DASHBOARD}
        </Heading>

        {/* Filters and Sort */}
        {renderFiltersAndSort()}

        {/* Content */}
        {isLoading && renderLoadingState()}
        {error && !isLoading && renderErrorState()}
        {!isLoading && !error && tickets.length === 0 && renderEmptyState()}
        {!isLoading && !error && tickets.length > 0 && renderTable()}

        {/* Pagination with results count */}
        {!isLoading && !error && renderPagination()}
      </VStack>
    </Layout>
  );
};

export default React.memo(ClosedTicketsDashboard);
